import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import agent from '../../app/api/agent';
import { fieldChecked, fieldValue, useModalOpen } from '../../app/components/polaris';
import type { ShopifyMetafieldAttributeOption } from '../../app/models/ShopifyMetafieldAttribute';
import type { StorefrontFilterDefinition, StorefrontFilterSettings, StorefrontUiSettings } from '../../app/models/StorefrontResultsSettings';
import '../settings/FitmentFieldsSettings.css';
import './ResultsFilterSettings.css';

const standard = [
    { value: 'vendors', label: 'Vendor' }, { value: 'productTypes', label: 'Product type' },
    { value: 'collectionIds', label: 'Collection' }, { value: 'tags', label: 'Tags' },
    { value: 'price', label: 'Price' }, { value: 'availability', label: 'Availability' },
    { value: 'fitmentTypes', label: 'Fitment type' },
];
const supportedTypes = ['single_line_text_field', 'list.single_line_text_field', 'boolean'];
export const legacyFilters = (showPrice: boolean): StorefrontFilterSettings => ({ enabled: true, items: [
    { key: 'availability', enabled: true, label: '', allowedValues: [] },
    { key: 'price', enabled: showPrice, label: '', allowedValues: [] },
] });

type Props = {
    ui: StorefrontUiSettings;
    pricesHidden: boolean;
    disabled?: boolean;
    update: (changes: Partial<StorefrontUiSettings>) => void;
    onConnectAttribute: () => void;
};

type FilterDragState = {
    sourceIndex: number;
    destinationIndex: number;
    left: number;
    top: number;
    width: number;
    portalContainer: HTMLElement;
};

export default function ResultsFilterSettings({ ui, pricesHidden, disabled, update, onConnectAttribute }: Props) {
    const [attributes, setAttributes] = useState<ShopifyMetafieldAttributeOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [retry, setRetry] = useState(0);
    const [selected, setSelected] = useState('');
    const [drag, setDrag] = useState<FilterDragState>();
    const rowRefs = useRef(new Map<number, HTMLDivElement>());
    const dragCleanup = useRef<() => void | undefined>(undefined);
    const [reordering, setReordering] = useState(false);
    const [editingKey, setEditingKey] = useState<string>();
    const [adding, setAdding] = useState(false);
    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true); setError(false);
            try {
                const values = await agent.ShopifyMetafieldAttributes.list({ enabledOnly: true, includeValues: false });
                if (active) setAttributes(values.filter(a => a.isEnabled && supportedTypes.includes(a.type)));
            } catch { if (active) setError(true); }
            finally { if (active) setLoading(false); }
        })();
        return () => { active = false; };
    }, [retry]);
    const config = ui.resultFilters ?? legacyFilters(ui.showPriceFilter);
    const choices = [...standard, ...attributes.map(a => ({ value: `attribute:${a.id}`, label: `${a.name} (${a.ownerType === 'Product' ? 'product' : 'variant'} attribute)` }))];
    const available = choices.filter(c => !config.items.some(i => i.key === c.value));
    const inactive = !!disabled || !config.enabled;
    useEffect(() => {
        if (inactive) {
            dragCleanup.current?.();
            setDrag(undefined);
        }
        return () => dragCleanup.current?.();
    }, [inactive]);
    const nameFor = (item: StorefrontFilterDefinition) => standard.find(c => c.value === item.key)?.label
        ?? attributes.find(a => item.key === `attribute:${a.id}`)?.name ?? 'Unavailable product attribute';
    const sourceFor = (item: StorefrontFilterDefinition) => {
        if (item.key === 'fitmentTypes') return 'PartFits fitment';
        const attribute = attributes.find(a => item.key === `attribute:${a.id}`);
        return attribute ? `${attribute.ownerType === 'Product' ? 'Product' : 'Variant'} attribute`
            : item.key.startsWith('attribute:') ? 'Product attribute' : 'Shopify product';
    };
    const save = (next: StorefrontFilterSettings) => update({ resultFilters: next });
    const edit = (index: number, changes: Partial<StorefrontFilterDefinition>) => save({ ...config, items: config.items.map((item, i) => i === index ? { ...item, ...changes } : item) });
    const move = (from: number, to: number) => {
        if (inactive || to < 0 || to >= config.items.length || from === to) return;
        const items = [...config.items]; items.splice(to, 0, items.splice(from, 1)[0]); save({ ...config, items });
    };
    const startDrag = (event: ReactPointerEvent<HTMLButtonElement>, sourceIndex: number) => {
        if (inactive || event.button !== 0 || dragCleanup.current) return;
        const row = rowRefs.current.get(sourceIndex);
        if (!row) return;
        const ownerDocument = row.ownerDocument, ownerWindow = ownerDocument.defaultView;
        const rect = row.getBoundingClientRect();
        const offsetX = event.clientX - rect.left, offsetY = event.clientY - rect.top;
        const pointerId = event.pointerId;
        const previousUserSelect = ownerDocument.body.style.userSelect;
        let current: FilterDragState = {
            sourceIndex, destinationIndex: sourceIndex, left: rect.left, top: rect.top,
            width: rect.width, portalContainer: ownerDocument.body,
        };
        const cleanup = () => {
            ownerDocument.removeEventListener('pointermove', onMove, true);
            ownerDocument.removeEventListener('pointerup', onUp, true);
            ownerDocument.removeEventListener('pointercancel', onCancel, true);
            ownerDocument.removeEventListener('keydown', onKeyDown, true);
            ownerWindow?.removeEventListener('blur', cancel, true);
            ownerDocument.body.style.userSelect = previousUserSelect;
            dragCleanup.current = undefined;
        };
        const cancel = () => { cleanup(); setDrag(undefined); };
        function onMove(pointer: PointerEvent) {
            if (pointer.pointerId !== pointerId) return;
            pointer.preventDefault();
            let destinationIndex = config.items.length - 1;
            for (let index = 0; index < config.items.length; index += 1) {
                const target = rowRefs.current.get(index)?.getBoundingClientRect();
                if (target && pointer.clientY < target.top + target.height / 2) {
                    destinationIndex = index;
                    break;
                }
            }
            current = { ...current, destinationIndex, left: pointer.clientX - offsetX, top: pointer.clientY - offsetY };
            setDrag(current);
        }
        function onUp(pointer: PointerEvent) {
            if (pointer.pointerId !== pointerId) return;
            cleanup();
            setDrag(undefined);
            move(sourceIndex, current.destinationIndex);
        }
        function onCancel(pointer: PointerEvent) { if (pointer.pointerId === pointerId) cancel(); }
        function onKeyDown(key: KeyboardEvent) {
            if (key.key === 'Escape') { key.preventDefault(); key.stopPropagation(); cancel(); }
        }
        event.preventDefault();
        event.stopPropagation();
        ownerDocument.body.style.userSelect = 'none';
        ownerDocument.addEventListener('pointermove', onMove, true);
        ownerDocument.addEventListener('pointerup', onUp, true);
        ownerDocument.addEventListener('pointercancel', onCancel, true);
        ownerDocument.addEventListener('keydown', onKeyDown, true);
        ownerWindow?.addEventListener('blur', cancel, true);
        dragCleanup.current = cleanup;
        setDrag(current);
    };
    const displayedFilters = [...config.items];
    if (drag) displayedFilters.splice(drag.destinationIndex, 0, displayedFilters.splice(drag.sourceIndex, 1)[0]);
    const renderFilterRow = (filter: StorefrontFilterDefinition, index: number, preview = false) => {
        const name = nameFor(filter), label = filter.label.trim() || name;
        const visible = config.enabled && filter.enabled && !(filter.key === 'price' && pricesHidden);
        return <div key={filter.key}
            ref={preview ? undefined : node => { if (node) rowRefs.current.set(index, node); else rowRefs.current.delete(index); }}
            className={`pf-selector-field${!preview && drag && config.items[drag.sourceIndex].key === filter.key ? ' pf-selector-field--dragging' : ''}${preview ? ' pf-selector-field--drag-preview' : ''}${editingKey === filter.key ? ' pf-selector-field--selected' : ''}`}>
            <button type="button" className="pf-selector-field__drag" aria-label={`Reorder ${label}`} aria-describedby="results-filter-reorder-help" disabled={inactive} tabIndex={preview ? -1 : undefined}
                onPointerDown={preview ? undefined : event => startDrag(event, index)}
                onClick={() => setReordering(true)} onKeyDown={event => {
                    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); move(index, index + (event.key === 'ArrowUp' ? -1 : 1)); }
                }}><s-icon type="drag-handle" /></button>
            <button type="button" className="pf-selector-field__select" aria-label={`Edit ${label} filter`} aria-haspopup="dialog" disabled={inactive} tabIndex={preview ? -1 : undefined} onClick={() => setEditingKey(filter.key)}>
                <span className="pf-selector-field__name"><span>{label}</span><small>{label !== name ? `${name} · ` : ''}{sourceFor(filter)}</small></span>
                <span className="pf-selector-field__rule">{visible ? 'Yes' : 'No'}</span><span className="pf-selector-field__edit">Edit</span>
            </button>
            {reordering && !preview && <div className="pf-selector-field__move">
                <s-button icon="arrow-up" variant="tertiary" accessibilityLabel={`Move ${label} up`} disabled={inactive || index === 0} onClick={() => move(index, index - 1)} />
                <s-button icon="arrow-down" variant="tertiary" accessibilityLabel={`Move ${label} down`} disabled={inactive || index === config.items.length - 1} onClick={() => move(index, index + 1)} />
            </div>}
        </div>;
    };
    const editingIndex = config.items.findIndex(item => item.key === editingKey);
    const item = config.items[editingIndex];
    const attribute = attributes.find(a => item?.key === `attribute:${a.id}`);
    const addOpen = adding && !inactive;
    const editOpen = !!item && !inactive;
    const closeAdd = () => setAdding(false);
    const closeEdit = () => setEditingKey(undefined);
    const addModalRef = useModalOpen(addOpen, closeAdd);
    const editModalRef = useModalOpen(editOpen, closeEdit);
    return <s-section heading="Filters">
        <s-stack gap="base">
            <s-switch label="Enable product filters" checked={config.enabled} disabled={disabled}
                onChange={event => save({ ...config, enabled: fieldChecked(event) })} />
            {error && <s-banner tone="warning">
                Connected attributes could not be loaded. Existing filter settings are kept.
                <s-button slot="secondary-actions" onClick={() => setRetry(retry + 1)}>Retry</s-button>
            </s-banner>}
            <div className={`pf-result-filters pf-selector-fields__list${reordering ? ' pf-selector-fields__list--reordering' : ''}`} role="group" aria-label="Product filters">
                <div className="pf-selector-fields__list-heading">
                    <s-stack direction="inline" gap="small-200" alignItems="center">
                        <s-heading>Product filters</s-heading>
                        <s-text color="subdued">{`${config.items.length} of 25`}</s-text>
                    </s-stack>
                    <s-button variant="tertiary" icon={reordering ? 'check' : 'sort'} disabled={inactive || config.items.length < 2}
                        onClick={() => setReordering(value => !value)}>{reordering ? 'Done reordering' : 'Reorder'}</s-button>
                </div>
                <div className="pf-selector-fields__column-head" aria-hidden="true"><span>Filter</span><span>Visible</span><span /></div>
                {displayedFilters.map((filter, index) => renderFilterRow(filter, index))}
                {!config.items.length && <div className="pf-selector-fields__empty"><s-text color="subdued">No filters yet</s-text></div>}
                <div className="pf-selector-fields__list-footer">
                    <s-button icon="plus" disabled={inactive || config.items.length >= 25 || !available.length} onClick={() => setAdding(true)}>Add filter</s-button>
                    <s-button variant="tertiary" disabled={disabled} onClick={onConnectAttribute}>Connect product attribute</s-button>
                </div>
            </div>
            <span className="pf-selector-fields__sr-only" id="results-filter-reorder-help">Drag to reorder, or use the up and down arrow keys.</span>
        </s-stack>
        {drag && createPortal(<div className="pf-result-filters pf-selector-fields__drag-overlay" aria-hidden="true"
            style={{ left: drag.left, top: drag.top, width: drag.width }}>
            {renderFilterRow(config.items[drag.sourceIndex], drag.sourceIndex, true)}
        </div>, drag.portalContainer)}
        <s-modal ref={addModalRef} id="pf-add-results-filter" heading="Add filter">
            {/* s-modal keeps its children mounted; render the form only while open. */}
            {addOpen && <s-select label="Filter to add" value={selected} onChange={event => setSelected(fieldValue(event))}>
                <s-option value="">{loading ? 'Loading attributes…' : 'Choose a filter'}</s-option>
                {available.map(choice => <s-option key={choice.value} value={choice.value}>{choice.label}</s-option>)}
            </s-select>}
            <s-button slot="primary-action" variant="primary" disabled={!available.some(c => c.value === selected) || config.items.length >= 25}
                onClick={() => {
                    save({ ...config, items: [...config.items, { key: selected, enabled: true, label: '', allowedValues: [] }] });
                    setAdding(false); setEditingKey(selected); setSelected('');
                }}>Add</s-button>
            <s-button slot="secondary-actions" onClick={closeAdd}>Cancel</s-button>
        </s-modal>
        <s-modal ref={editModalRef} id="pf-edit-results-filter" heading={item ? `${nameFor(item)} filter` : 'Filter'}>
            {editOpen && <s-stack gap="base">
                <s-text color="subdued">{sourceFor(item)}</s-text>
                <s-checkbox label="Show this filter" checked={item.enabled} onChange={event => edit(editingIndex, { enabled: fieldChecked(event) })} />
                <s-text-field label="Display label" value={item.label} placeholder={nameFor(item)} maxLength={80} autocomplete="off"
                    onInput={event => edit(editingIndex, { label: fieldValue(event) })} />
                {item.key === 'tags' && <s-text-area label="Allowed tags (one per line)" rows={3} value={item.allowedValues.join('\n')}
                    onInput={event => edit(editingIndex, { allowedValues: fieldValue(event).split('\n') })} />}
                {item.key === 'price' && pricesHidden && <s-banner tone="info">The price filter is hidden while product prices are hidden.</s-banner>}
                {item.key.startsWith('attribute:') && !attribute && !loading && !error && <s-banner tone="warning">This attribute is disconnected or uses an unsupported type.</s-banner>}
                {attribute && attribute.lastIndexedAt == null && <s-banner tone="info">Sync this attribute’s values in Shopify sync before it can appear.</s-banner>}
            </s-stack>}
            <s-button slot="primary-action" variant="primary" onClick={closeEdit}>Done</s-button>
            <s-button slot="secondary-actions" tone="critical" onClick={() => {
                if (item) save({ ...config, items: config.items.filter(filter => filter.key !== item.key) });
                closeEdit();
            }}>Remove filter</s-button>
        </s-modal>
    </s-section>;
}
